import { ORG_LEVELS } from '@/utils/constants/organization-matrix';
import type { IOrgChart, BulkOrgChartNode } from '@/states/organization.state';

export interface OrgTableRow {
  tempKey: string;
  id?: number;
  name: string;
  division_key: string;
  level_key: string;
  description: string;
  parentTempKey: string | null;
}

let keyCounter = 0;
export const newTempKey = (): string => `row-${Date.now()}-${keyCounter++}`;

// Converts live table rows into a flat IOrgChart list for real-time preview.
// Sets parent_id using the assigned numeric id per row so that OrgChartTreeView's
// internal buildIOrgChartTree call can reconstruct the hierarchy correctly.
export const rowsToIOrgChartTree = (rows: OrgTableRow[]): IOrgChart[] => {
  // Assign a stable numeric id to each row (use existing id or a negative temp id)
  const idByTempKey = new Map<string, number>();
  rows.forEach((r, idx) => {
    idByTempKey.set(r.tempKey, r.id ?? -(idx + 1));
  });

  return rows.map(r => ({
    id: idByTempKey.get(r.tempKey)!,
    name: r.name,
    division_key: r.division_key,
    level_key: r.level_key,
    description: r.description,
    is_published: true,
    tenant_id: 0,
    parent_id: r.parentTempKey ? idByTempKey.get(r.parentTempKey) ?? null : null,
    children: [],
  }));
};

export const levelRankByKey = (levelKey: string): number => {
  const idx = ORG_LEVELS.findIndex(l => l.key === levelKey);
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
};

export const flattenTreeToRows = (nodes: IOrgChart[]): OrgTableRow[] => {
  const rows: OrgTableRow[] = [];
  const walk = (node: IOrgChart, parentTempKey: string | null) => {
    const tempKey = newTempKey();
    rows.push({
      tempKey,
      id: node.id,
      name: node.name ?? '',
      division_key: node.division_key ?? '',
      level_key: node.level_key ?? '',
      description: node.description ?? '',
      parentTempKey,
    });
    (node.children ?? []).forEach(child => walk(child, tempKey));
  };
  (nodes ?? []).forEach(root => walk(root, null));
  return rows;
};

export const rowsToBulkPayload = (rows: OrgTableRow[]): BulkOrgChartNode[] =>
  rows.map(r => ({
    tempKey: r.tempKey,
    ...(r.id != null ? { id: r.id } : {}),
    name: r.name.trim(),
    division_key: r.division_key,
    level_key: r.level_key,
    description: r.description?.trim() ? r.description.trim() : null,
    parentTempKey: r.parentTempKey,
  }));

const descendantKeys = (rows: OrgTableRow[], tempKey: string): Set<string> => {
  const out = new Set<string>();
  const childrenOf = (k: string) => rows.filter(r => r.parentTempKey === k);
  const stack = childrenOf(tempKey);
  while (stack.length) {
    const r = stack.pop()!;
    if (!out.has(r.tempKey)) {
      out.add(r.tempKey);
      stack.push(...childrenOf(r.tempKey));
    }
  }
  return out;
};

export const getParentOptions = (rows: OrgTableRow[], tempKey: string): OrgTableRow[] => {
  const blocked = descendantKeys(rows, tempKey);
  return rows.filter(r => r.tempKey !== tempKey && !blocked.has(r.tempKey));
};

export const validateOrgTableRows = (rows: OrgTableRow[]): string | null => {
  if (rows.length === 0) return 'Minimal 1 jabatan wajib diisi';
  const highestLevelRank = Math.min(...rows.map(r => levelRankByKey(r.level_key)));
  const byKey = new Map(rows.map(r => [r.tempKey, r]));
  for (const r of rows) {
    if (!r.name.trim()) return 'Nama jabatan wajib diisi pada setiap baris';
    if (!r.division_key) return `Divisi wajib dipilih untuk "${r.name || '-'}"`;
    if (!r.level_key) return `Level wajib dipilih untuk "${r.name || '-'}"`;
    if (r.parentTempKey == null) {
      if (levelRankByKey(r.level_key) > highestLevelRank) {
        return `"${r.name}" wajib memiliki atasan karena bukan level tertinggi di struktur`;
      }
    } else {
      const parent = byKey.get(r.parentTempKey);
      if (!parent) return `Atasan tidak valid untuk "${r.name}"`;
      if (parent.tempKey === r.tempKey) return `"${r.name}" tidak boleh menjadi atasannya sendiri`;
      if (levelRankByKey(r.level_key) <= levelRankByKey(parent.level_key)) {
        return `Level "${r.name}" harus di bawah level atasannya`;
      }
    }
  }
  for (const start of rows) {
    let cur: OrgTableRow | undefined = start;
    const seen = new Set<string>();
    while (cur && cur.parentTempKey != null) {
      if (seen.has(cur.tempKey)) return 'Struktur memiliki atasan yang melingkar';
      seen.add(cur.tempKey);
      cur = byKey.get(cur.parentTempKey);
    }
  }
  return null;
};
